import React, { useState } from "react";
import axios from "axios";
import { X, Copy, Check, Link2, Shield } from "lucide-react";
import { T } from "../theme";

/**
 * PrivacySettingsModal — who can see my profile / collection / activity,
 * search opt-in, and the shareable /user/:username link.
 *
 * Saves via PATCH /api/user/:userId/privacy. Cancel discards unsaved changes.
 */

const API_URL = process.env.REACT_APP_API_URL || "";

const LEVELS = [
  { key: "private", label: "Private",      emoji: "🔒" },
  { key: "friends", label: "Friends Only", emoji: "🤝" },
  { key: "public",  label: "Public",       emoji: "🌐" },
];

const LEVEL_HELP = {
  private: "Only you",
  friends: "You + accepted friends",
  public:  "Anyone with the link",
};

function ToggleGroup({ value, onChange }) {
  return (
    <div>
      <div style={{
        display: "flex", gap: 4, padding: 4,
        background: "rgba(255,255,255,0.03)", borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.06)",
      }}>
        {LEVELS.map((lvl) => {
          const active = value === lvl.key;
          return (
            <button
              key={lvl.key}
              onClick={() => onChange(lvl.key)}
              style={{
                flex: 1, padding: "9px 4px", minHeight: 40,
                border: active ? "1px solid rgba(180,79,255,0.5)" : "1px solid transparent",
                borderRadius: 9,
                background: active
                  ? "linear-gradient(135deg, rgba(180,79,255,0.28), rgba(255,60,172,0.28))"
                  : "transparent",
                color: active ? T.textPrimary : T.textMuted,
                fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
                transition: "all 0.15s", whiteSpace: "nowrap",
              }}
            >
              {lvl.emoji} {lvl.label}
            </button>
          );
        })}
      </div>
      <p style={{ margin: "6px 2px 0", color: "rgba(255,255,255,0.3)", fontSize: "0.72rem" }}>
        {LEVEL_HELP[value]}
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{
        margin: "0 0 8px", fontSize: "0.78rem", fontWeight: 700,
        color: T.textPrimary,
      }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function PrivacySettingsModal({ userId, username, initial, onSaved, onClose }) {
  const [settings, setSettings] = useState({
    profileVisibility:    initial?.profileVisibility    || "private",
    collectionVisibility: initial?.collectionVisibility || "private",
    activityVisibility:   initial?.activityVisibility   || "private",
    showInSearch:         initial?.showInSearch          ?? false,
  });
  const [saving, setSaving]   = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError]     = useState("");
  const [copied, setCopied]   = useState(false);

  const profileUrl = `${window.location.origin}/user/${encodeURIComponent(username)}`;

  const set = (key) => (val) => setSettings((s) => ({ ...s, [key]: val }));

  const handleCopy = () => {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await axios.patch(`${API_URL}/api/user/${userId}/privacy`, { ...settings, viewerId: userId });
      if (res.data.success) {
        setSavedOk(true);
        onSaved?.(res.data.privacy);
        setTimeout(onClose, 900);
      }
    } catch (err) {
      console.error("Save privacy error:", err);
      setError(err.response?.data?.error || "Failed to save privacy settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="kk-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 999,
        background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        className="kk-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "linear-gradient(160deg, #0a0515, #0a0a0f)",
          border: "1px solid rgba(255,255,255,0.12)", borderRadius: 24,
          padding: "28px 24px", maxWidth: 440, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Shield size={17} color={T.purple} style={{ filter: `drop-shadow(0 0 6px ${T.purple})` }} />
            <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: T.textPrimary }}>
              Privacy
            </h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6 }}>
            <X size={18} color={T.textMuted} />
          </button>
        </div>

        <Section title="Who can see my profile?">
          <ToggleGroup value={settings.profileVisibility} onChange={set("profileVisibility")} />
        </Section>

        <Section title="Who can see my collection?">
          <ToggleGroup value={settings.collectionVisibility} onChange={set("collectionVisibility")} />
        </Section>

        <Section title="Who can see my activity?">
          <ToggleGroup value={settings.activityVisibility} onChange={set("activityVisibility")} />
        </Section>

        <Section title="Appear in search">
          <button
            onClick={() => set("showInSearch")(!settings.showInSearch)}
            style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%",
              padding: "12px 14px", minHeight: 44, borderRadius: 12, cursor: "pointer",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)",
              textAlign: "left",
            }}
          >
            {/* Track */}
            <span style={{
              width: 40, height: 22, borderRadius: 20, flexShrink: 0,
              background: settings.showInSearch
                ? "linear-gradient(135deg, #ff3cac, #b44fff)"
                : "rgba(255,255,255,0.1)",
              position: "relative", transition: "background 0.2s",
            }}>
              <span style={{
                position: "absolute", top: 2,
                left: settings.showInSearch ? 20 : 2,
                width: 18, height: 18, borderRadius: "50%",
                background: "#fff", transition: "left 0.2s",
              }} />
            </span>
            <span style={{ color: settings.showInSearch ? T.textPrimary : T.textMuted, fontSize: "0.82rem", fontWeight: 600 }}>
              {settings.showInSearch ? "Discoverable in search" : "Hidden from search"}
            </span>
          </button>
          <p style={{ margin: "6px 2px 0", color: "rgba(255,255,255,0.3)", fontSize: "0.72rem" }}>
            When off, people can only find you via direct link or friend request
          </p>
        </Section>

        <Section title="Your Profile Link">
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 12px", borderRadius: 12,
            background: "rgba(0,245,255,0.04)",
            border: "1px solid rgba(0,245,255,0.18)",
          }}>
            <Link2 size={13} color={T.cyan} style={{ flexShrink: 0 }} />
            <span style={{
              flex: 1, minWidth: 0, color: T.cyan, fontSize: "0.78rem",
              fontFamily: "monospace",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {profileUrl}
            </span>
            <button
              onClick={handleCopy}
              style={{
                display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
                padding: "7px 12px", borderRadius: 9, cursor: "pointer",
                background: copied ? "rgba(57,255,20,0.12)" : "rgba(255,255,255,0.06)",
                border: copied ? "1px solid rgba(57,255,20,0.35)" : "1px solid rgba(255,255,255,0.12)",
                color: copied ? T.green : T.textMuted,
                fontSize: "0.72rem", fontWeight: 700,
              }}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <p style={{ margin: "6px 2px 0", color: "rgba(255,255,255,0.3)", fontSize: "0.72rem" }}>
            Anyone with this link can send you a friend request, even if your profile is private
          </p>
        </Section>

        {error && (
          <div style={{
            marginBottom: 14, padding: "10px 14px",
            background: "rgba(255,60,60,0.1)",
            border: "1px solid rgba(255,60,60,0.3)",
            borderRadius: 10, color: "#ff8080", fontSize: "0.82rem",
          }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "12px", minHeight: 44,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12, color: "rgba(255,255,255,0.7)",
              fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || savedOk}
            style={{
              flex: 2, padding: "12px", minHeight: 44,
              background: savedOk
                ? "rgba(57,255,20,0.15)"
                : "linear-gradient(135deg, #ff3cac, #b44fff)",
              border: savedOk ? "1px solid rgba(57,255,20,0.4)" : "none",
              borderRadius: 12, color: savedOk ? T.green : "#fff",
              fontWeight: 700, fontSize: "0.9rem",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              boxShadow: savedOk ? "none" : "0 4px 20px rgba(180,79,255,0.35)",
            }}
          >
            {savedOk ? "Saved ✓" : saving ? "Saving…" : "Save Privacy Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrivacySettingsModal;
